import React, { useState } from "react";

import Input from "../../controls/Input";
import CreatableSelectMenu from "../../controls/CreatableSelectMenu";
import SelectMenu from "../../controls/SelectMenu";

import {
  classNames,
  convertToDefEventPara,
  wipStatusOptions,
  formatDateWithTime,
  generateUniqueNumber,
  yesOrNoOptions,
  receivingStatusOptions,
} from "../../../shared/utility";
import AddItemToReceivingForm from "./AddItemToReceivingForm";
import MediaUi from "../../importMedia/MediaUi";

export default function ReceivingInvoiceFields(props) {
  const {
    handleInputChange,
    values,
    errors,
    carrierOptions,
    truckOptions,
    fetchPosForTruck,
    supplierOptions,
    productOptions,
    onFetchProductDetails,
    setEmptyModelOpen,
    handleSubmit,
    submitInProgress,
    addItemToReceiving,
    employeeOptions,
    getWarehouses,
    getWarehouseLocations,
    closed,
    setValues
  } = props;

  const [pdf, setPdf] = useState(null);

  const fetchProductDetailsAndOpenPopup = async function (productId) {
    const result = await onFetchProductDetails(productId);
    if (result.success) {
      const productDetails = result.data;

      setEmptyModelOpen({
        open: true,
        childComponent: (
          <AddItemToReceivingForm
            product={productDetails}
            addItemToReceiving={addItemToReceiving}
            supplierNumber={values.supplier_selected.supplier_number}
            getWarehouses={getWarehouses}
            getWarehouseLocations={getWarehouseLocations}
          />
        ),
        size: "sm:max-w-3xl",
      });
    }
  };

  return (
    <div className="pt-6 pb-5 grid grid-cols-1 gap-2">
      <div className="p-4 card-custom space-y-5">
        <div className="grid grid-cols-7 gap-4">
          <div>
            <CreatableSelectMenu
              handleInputChange={(e) => {
                if (!e.target.value.__isNew__) {
                  fetchPosForTruck(e.target.value.value);
                }

                handleInputChange(e);
              }}
              name="truck_selected"
              options={truckOptions}
              value={values.truck_selected}
              title={"Truck #"}
              error={errors.truck_selected}
              disabled={closed}
            />
            <button
              type="button"
              className="text-sm text-purple-600 font-bold"
              onClick={() => {
                const generatedTruckNumber = generateUniqueNumber();
                handleInputChange(
                  convertToDefEventPara("truck_selected", {
                    value: 0,
                    label: generatedTruckNumber,
                    __isNew__: true,
                  })
                );
              }}
            >
              Generate
            </button>
          </div>
          <Input
            label="Invoice #"
            name="invoice_number"
            onChange={handleInputChange}
            value={values.invoice_number}
            labelOn={true}
            type="text"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
            error={errors.invoice_number}
            disabled={closed}
          />
          <SelectMenu
            handleInputChange={handleInputChange}
            name="supplier_selected"
            options={supplierOptions}
            value={values.supplier_selected}
            title={"Supplier"}
            error={errors.supplier_selected}
            parentClass="col-span-2"
            disabled={closed}
          />
          <SelectMenu
            handleInputChange={handleInputChange}
            name="carrier_selected"
            options={carrierOptions}
            value={values.carrier_selected}
            title={"Carrier"}
            error={errors.carrier_selected}
            disabled={closed}
          />
          <Input
            label="Created"
            name="created_date"
            value={formatDateWithTime(values.created_date)}
            labelOn={true}
            type="text"
            disabled={true}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm w-48"
            error={errors.created_date}
          />
          <SelectMenu
            name="statusSelected"
            options={receivingStatusOptions}
            value={values.statusSelected}
            handleInputChange={handleInputChange}
            title={"Status"}
            error={errors.statusSelected}
          />
        </div>
        <div className="grid grid-cols-4 gap-4">
          <SelectMenu
            handleInputChange={handleInputChange}
            name="created_by"
            options={employeeOptions}
            value={values.created_by}
            title={"Created by"}
            error={errors.created_by}
            disabled={closed}
          />
          <SelectMenu
            name="truck_question_1"
            options={yesOrNoOptions}
            value={values.truck_question_1}
            handleInputChange={handleInputChange}
            title="Truck is clean, no foul odour or condensation present"
            error={errors.truck_question_1}
            disabled={closed}
          />
          <SelectMenu
            name="truck_question_2"
            options={yesOrNoOptions}
            value={values.truck_question_2}
            handleInputChange={handleInputChange}
            title="Truck has no rust, mold, flaking paint. No damage to floors, walls or ceiling"
            error={errors.truck_question_2}
            disabled={closed}
          />
          <SelectMenu
            name="truck_question_3"
            options={yesOrNoOptions}
            value={values.truck_question_3}
            handleInputChange={handleInputChange}
            title="Truck doors are sealed properly to prevent entrance of pests, dust and foreign material during transport"
            error={errors.truck_question_3}
            disabled={closed}
          />
          <SelectMenu
            name="truck_question_4"
            options={yesOrNoOptions}
            value={values.truck_question_4}
            handleInputChange={handleInputChange}
            title={"Product condition is acceptable"}
            error={errors.truck_question_4}
            disabled={closed}
          />
          <SelectMenu
            handleInputChange={(e) => {
              fetchProductDetailsAndOpenPopup(e.target.value.value);
              handleInputChange(e);
            }}
            name="product_selected"
            options={productOptions}
            value={values.product_selected}
            title={"Products"}
            parentClass={classNames(values.id > 0 ? "col-span-2" : "hidden")}
            disabled={closed}
          />
          <MediaUi
            title="Document"
            uploadedImage={pdf}
            setUploadedImage={setPdf}
            isEdit={true}
            imageUrl={values.pdf_url}
            imageName="uploaded_pdf"
            handleInputChange={handleInputChange}
            onDelete={() => {
              setValues({
                ...values,
                uploaded_pdf: null,
                pdf: "",
                pdf_url: null,
              });
              setPdf(null);
            }}
          />
        </div>
        <button
          type="submit"
          className={classNames(
            values.id > 0
              ? "hidden"
              : "inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          )}
          onClick={handleSubmit}
        >
          {submitInProgress ? (
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : null}
          Create
        </button>
      </div>
    </div>
  );
}
